// ─────────────────────────────────────────────
//  Grid domain — daily-assignment grid layout (no Firebase)
//  Owns: section building from the facility's Locations + Rooms, room↔assignment
//  matching, and the CRNA call list builder. Mirrors the breaks.js / schedule.js
//  pattern: pure functions, no I/O, fully unit-tested.
// ─────────────────────────────────────────────
import { groupRoomsByLocation, resolveLocations, isTbdRoom, isTbdRoomName } from "./locations";
import { ROOM_NAME_ALIASES, rankRoomMatch, resolveRoom } from "./roomResolver";

// ──────────────────────────────────────
//  GRID_COLUMNS — column order for the daily grid. Relief columns were removed
//  (relief is handled live on the board, not pre-scheduled in the grid).
// ──────────────────────────────────────
export const GRID_COLUMNS = [
  "OR",
  "Start",
  "End",
  "SURGEON",
  "CRNA",
  "MD/DO",
];

// ──────────────────────────────────────
//  buildAssignmentSections
//
//  Builds the daily-assignment grid from the facility's own Locations + Rooms
//  (defined in Facility Settings) instead of any hardcoded sheet. Returns an
//  annotated section list: [{ key, label, rows: [{ or, assignment }] }].
//
//  Rules:
//  - Sections + order + labels come from `groupRoomsByLocation(rooms, locations)`
//    — the exact same grouping the OR Board uses, so both views stay in sync.
//  - One row per room; `or` is the room's name, `assignment` is the day's doc
//    whose orRoom matches that room (via assignmentForRoom's name/alias rules),
//    or null when nothing matches.
//  - Any assignment matching no room is appended in a trailing
//    { key: "other", label: "OTHER" } section, natural-sorted by orRoom, so a
//    row is never silently dropped (e.g. a room deleted after it was assigned).
//  - If no OTHER assignments exist, no OTHER section is appended.
// ──────────────────────────────────────
export function buildAssignmentSections(rooms, locations, assignments) {
  // TBD rooms (and any TBD assignment) are an internal placeholder — never
  // shown on the grid, and no provider is visibly assigned to them.
  const roomList = (Array.isArray(rooms) ? rooms : []).filter((r) => !isTbdRoom(r));
  const assignmentList = (Array.isArray(assignments) ? assignments : []).filter(
    (a) => !isTbdRoomName(a && a.orRoom)
  );

  // Ordered location sections, identical to the board's grouping. Resolve the
  // facility list (falling back to defaults) so labels are correct even if the
  // caller passes an unset/empty list.
  const grouped = groupRoomsByLocation(roomList, resolveLocations(locations));

  // Track assignments claimed by a room so the rest fall through to OTHER.
  const claimedIds = new Set();

  const sections = grouped.map((section) => ({
    key: section.key,
    label: section.label,
    rows: section.rooms.map((room) => {
      const assignment = assignmentForRoom(room.name, assignmentList);
      if (assignment) {
        claimedIds.add(assignment.id ?? assignment);
        return { or: room.name, assignment };
      }
      // No saved assignment doc — mirror the room's LIVE board staffing (a
      // provider signed in / was assigned on the OR Board writes to the room
      // doc, not the assignments collection). Keeps Daily Assignments in sync
      // with the Full Board. Unstaffed rooms stay null (blank template row).
      return { or: room.name, assignment: syntheticAssignmentFromRoom(room) };
    }),
  }));

  // OTHER: every assignment not matched to a current room.
  const overflow = assignmentList.filter((a) => !claimedIds.has(a?.id ?? a));
  if (overflow.length > 0) {
    overflow.sort((a, b) =>
      (a?.orRoom ?? "").localeCompare(b?.orRoom ?? "", undefined, {
        numeric: true,
      })
    );
    sections.push({
      key: "other",
      label: "OTHER",
      rows: overflow.map((assignment) => ({
        or: assignment?.orRoom ?? "",
        assignment,
      })),
    });
  }

  return sections;
}

// ──────────────────────────────────────
//  syntheticAssignmentFromRoom
//
//  Bridges the live OR Board state into the daily-assignment grid. Board
//  sign-in / setRoomAssignment writes the working provider + supervisor onto
//  the ROOM doc (providerId, supervisingDoctorId), never the assignments
//  collection. When a room has no saved assignment doc for the day, this
//  returns an assignment-shaped object derived from the room's live fields so
//  the grid row displays the same staff the board shows.
//
//  - Returns null when the room carries no live staffing (blank template row).
//  - Carries NO `id`, so saving the row creates a fresh assignment doc
//    (materialising the live state) instead of updating a non-existent one.
//  - ahpName/attendingName ride along so the grid's orphan-option fallback can
//    render names even for staff not on today's roster dropdown lists.
// ──────────────────────────────────────
export function syntheticAssignmentFromRoom(room) {
  if (!room) return null;
  const ahpId = room.providerId || null;
  const attendingId = room.supervisingDoctorId || null;
  if (!ahpId && !attendingId) return null;
  return {
    orRoom: room.name || "",
    ahpId,
    ahpName: room.providerName || "",
    attendingId,
    attendingName: room.supervisorName || "",
    source: "live-room",
  };
}

// ──────────────────────────────────────
//  ROOM_NAME_ALIASES — re-exported. The map itself now lives in
//  roomResolver.js beside the ranking that uses it, because it is a MATCHING
//  rule and because every one of its keys is a legacy seed room whose alias
//  target duplicates a current room's exact name. Importers unchanged.
// ──────────────────────────────────────
export { ROOM_NAME_ALIASES };

// ──────────────────────────────────────
//  assignmentForRoom
//
//  Finds the day's assignment doc for a live board room. Board rooms are
//  named like "OR 3" while assignment orRoom labels are "3" (or "WC E2"),
//  so we match the full normalised name, the name with a leading "OR "
//  prefix stripped, or a ROOM_NAME_ALIASES mapping. Returns null when
//  nothing matches.
// ──────────────────────────────────────
export function assignmentForRoom(roomName, assignments) {
  return assignmentsForRoom(roomName, assignments)[0] || null;
}

// ──────────────────────────────────────
//  assignmentsForRoom — EVERY row for a room, not just the first.
//
//  Same matching rules as assignmentForRoom, applied in the same priority
//  order, but returning the whole set. A room legitimately has more than one
//  row: a morning case and an afternoon case, or a split OB day/night pair.
// ──────────────────────────────────────
export function assignmentsForRoom(roomName, assignments) {
  const list = Array.isArray(assignments) ? assignments : [];
  if (!_normalise(roomName)) return [];
  // ONE ranking function, shared with roomResolver's reverse lookup, so a room
  // resolved from a label and a row matched from a room can never disagree.
  return list
    .map((a, i) => ({ a, i, r: a ? rankRoomMatch(roomName, a.orRoom) : null }))
    .filter((x) => x.r != null)
    .sort((x, y) => (x.r !== y.r ? x.r - y.r : x.i - y.i))
    .map((x) => x.a);
}

// ──────────────────────────────────────
//  roomForOrRoom
//
//  Reverse of assignmentForRoom: given an assignment's orRoom label, find the
//  live room doc it belongs to (by full name, OR-prefix-stripped name, or
//  legacy alias). Used by the Daily grid to mirror a saved row onto its room
//  doc so the OR Board reflects grid assignments (parity with AssignmentModal).
//  Returns null when no room matches (e.g. a free OTHER row).
// ──────────────────────────────────────
export function roomForOrRoom(orRoom, rooms) {
  const verdict = resolveRoom(orRoom, rooms);
  // ⚠️ `ambiguous` deliberately returns null. Two rooms claiming a label with
  // equal strength is a review item naming both, not a coin flip on which
  // theatre a case is in. Callers that need to SAY SO — and every unattended
  // importer should — call resolveRoom/roomReviewItem directly instead.
  return verdict.status === "resolved" ? verdict.room : null;
}

// ──────────────────────────────────────
//  hasAssignmentData
//
//  True when an assignment doc carries real schedule content — staff, a
//  surgeon, or a start time. Empty materialized docs (id only) don't count.
//  The board uses this to surface scheduled rooms before anyone signs in.
// ──────────────────────────────────────
export function hasAssignmentData(assignment) {
  return !!(
    assignment &&
    (assignment.surgeon ||
      assignment.attendingId ||
      assignment.ahpId ||
      assignment.startTime)
  );
}

// ──────────────────────────────────────
//  CALL_LIST_SIZE — always 22 numbered slots on the WC sheet.
// ──────────────────────────────────────
export const CALL_LIST_SIZE = 22;

// ──────────────────────────────────────
//  buildCallList
//
//  Produces exactly 22 slots: { n, crna, mdCode, loc }.
//
//  Parameters
//  - callListIds  : persisted ordered array of CRNA member ids (may be shorter
//                   than 22 or contain nulls — we pad the rest with null slots).
//  - crnas        : array of member objects with { id, ... }.
//  - assignments  : array of assignment docs with { ahpId, orRoom, attendingId }.
//  - doctors      : array of doctor objects with { id, shortCode, displayName }.
//
//  Derivation rules (MD and LOC are NEVER stored in the call list directly):
//  - crna     : member from `crnas` matching the id, or null for empty slots.
//  - loc      : that CRNA's assignment orRoom, or "" when unassigned.
//  - mdCode   : doctor shortCode (falling back to displayName) for the
//               attendingId on that assignment row, or "" when unassigned.
// ──────────────────────────────────────
export function buildCallList(callListIds, crnas, assignments, doctors) {
  const idList = Array.isArray(callListIds) ? callListIds : [];
  const crnaList = Array.isArray(crnas) ? crnas : [];
  const assignmentList = Array.isArray(assignments) ? assignments : [];
  const doctorList = Array.isArray(doctors) ? doctors : [];

  // Index lookups.
  const crnaById = new Map(crnaList.map((c) => [c.id, c]));
  const assignmentByCrnaId = new Map(
    assignmentList.map((a) => [a.ahpId, a])
  );
  const doctorById = new Map(doctorList.map((d) => [d.id, d]));

  const slots = [];

  for (let i = 0; i < CALL_LIST_SIZE; i++) {
    const id = idList[i] ?? null;
    const crna = id ? (crnaById.get(id) ?? null) : null;

    let mdCode = "";
    let loc = "";

    if (crna) {
      const assignment = assignmentByCrnaId.get(crna.id);
      if (assignment) {
        loc = assignment.orRoom ?? "";
        const doctor = assignment.attendingId
          ? doctorById.get(assignment.attendingId)
          : null;
        if (doctor) {
          mdCode = doctor.shortCode || doctor.displayName || "";
        }
      }
    }

    slots.push({ n: i + 1, crna, mdCode, loc });
  }

  return slots;
}

// ──────────────────────────────────────
//  Internal helpers
// ──────────────────────────────────────

// Normalise a room label for matching: trim and lowercase.
function _normalise(label) {
  return String(label ?? "").trim().toLowerCase();
}
